import React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import useVoyagerStore from '../stores/useVoyagerStore';
import SimpleSidebar, { SIMPLE_SIDEBAR_WIDTH } from './SimpleSidebar';
import { PHONE_QUERY, useChatSidebar } from './chatSidebarState';
import { hasNews, newlyFinished, onScreen, useFinishedToast, useSimpleSidebar } from './simpleSidebarState';
import FinishedToastView from './FinishedToast';
import BuildToastView from './BuildToast';
import { startSummary } from '../components/Builds/buildsSummary';
import SearchPalette from './SearchPalette';
import { useSearchPalette } from './searchPaletteState';

export const FINISHED_TOAST_MS = 7000;

/** Shows the sidebar again; a dot while a thread not on screen is running, waits on you or finished. */
function ShowButton() {
  const placeholder = useSimpleSidebar((s) => s.placeholder);
  const awayDone = useSimpleSidebar((s) => s.awayDone);
  const dot = useVoyagerStore((s) => hasNews(s as any, placeholder, awayDone));
  const phone = useMediaQuery(PHONE_QUERY);
  return <Tooltip title="Show the sidebar">
    <IconButton size="small" aria-label={dot ? 'Show the sidebar (a chat has news)' : 'Show the sidebar'} data-testid="simple-show-sidebar" data-dot={dot ? 'on' : undefined}
      onClick={() => (phone ? useChatSidebar.getState().setDrawerOpen(true) : useSimpleSidebar.getState().setCollapsed(false))}
      sx={{ color: 'var(--ink-2)', borderRadius: '7px', position: 'relative',
        ...(dot ? { '&::after': { content: '""', position: 'absolute', top: 5, right: 5, width: 7, height: 7, borderRadius: '50%', bgcolor: 'var(--acc)', boxShadow: '0 0 0 2px var(--bg)' } } : {}) }}>
      <ViewSidebarOutlinedIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
    </IconButton>
  </Tooltip>;
}

/**
 * Watches the conversations. A parked thread that turns unread finished while you were
 * elsewhere; so did the current thread when its run ended while you were on another
 * page (it is not parked: `awayDone` marks it until you come back to it, and it is
 * marked unread if you open another thread first). Either shows the toast.
 */
function useFinishedWatcher() {
  React.useEffect(() => {
    let timer: number | undefined;
    let quiet = false;
    const announce = (threadId: string, generation: number) => {
      useFinishedToast.setState({ toast: { threadId, generation } });
      window.clearTimeout(timer);
      timer = window.setTimeout(() => useFinishedToast.setState({ toast: null }), FINISHED_TOAST_MS);
    };
    // Back on a thread, its news is read: its "done" and its toast go. Checked once the navigation in
    // progress has finished (a microtask later), so passing through the landing view on the way to another
    // page (New chat, a placeholder left for Knowledge) is never taken for having seen it.
    let pending = false;
    const settle = () => {
      if (pending) return;
      pending = true;
      void Promise.resolve().then(() => {
        pending = false;
        const s = useVoyagerStore.getState();
        const { awayDone, placeholder } = useSimpleSidebar.getState();
        if (!s.currentThreadId || !onScreen(s, placeholder)) return;
        if (awayDone === s.currentThreadId) useSimpleSidebar.setState({ awayDone: null });
        if (useFinishedToast.getState().toast?.threadId === s.currentThreadId) useFinishedToast.setState({ toast: null });
      });
    };
    const offStore = useVoyagerStore.subscribe((s, prev) => {
      const { awayDone, placeholder } = useSimpleSidebar.getState();
      // the current thread left the screen with its "done" unread: the parked copy keeps it
      if (awayDone && prev.currentThreadId === awayDone && s.currentThreadId !== awayDone) {
        useSimpleSidebar.setState({ awayDone: null });
        const left = s.parkedConversations[awayDone];
        if (left && !left.unread) {
          // already announced when it finished: marking it is not news
          quiet = true;
          try { useVoyagerStore.setState({ parkedConversations: { ...s.parkedConversations, [awayDone]: { ...left, unread: true } } } as any); } finally { quiet = false; }
          return;
        }
      }
      if (!quiet && s.parkedConversations !== prev.parkedConversations) {
        const ids = newlyFinished(prev.parkedConversations, s.parkedConversations);
        if (ids.length) announce(ids[ids.length - 1], s.threadsGeneration);
      }
      const id = s.currentThreadId;
      // a run's end, not its parking: parking resets the live status to idle once the parked copy holds the run
      if (id && (prev.currentThreadId === id || prev.currentThreadId === null) && prev.landingAgentStatus === 'running'
        && !['running', 'cancelled', 'idle'].includes(s.landingAgentStatus) && !s.parkedConversations[id] && !onScreen(s, placeholder)) {
        useSimpleSidebar.setState({ awayDone: id });
        announce(id, s.threadsGeneration);
      }
      settle();
    });
    const offSidebar = useSimpleSidebar.subscribe((s, prev) => { if (s.placeholder !== prev.placeholder) settle(); });
    return () => { offStore(); offSidebar(); window.clearTimeout(timer); useFinishedToast.setState({ toast: null }); useSimpleSidebar.setState({ awayDone: null }); };
  }, []);
}

/**
 * The simplified app's shell (#1500), rendered inside the shell's one root:
 * the one sidebar (#1503) beside every page — hidden to a slim strip with its
 * toggle on a wide screen, a drawer on a phone — and the finished-elsewhere toast.
 * Resolving the flag never remounts the page: the page is a sibling of this.
 */
export default function SimpleShell() {
  const phone = useMediaQuery(PHONE_QUERY);
  const collapsed = useSimpleSidebar((s) => s.collapsed);
  const drawerOpen = useChatSidebar((s) => s.drawerOpen);
  useFinishedWatcher();
  // the viewer's own builds (#1625): one poller for the sidebar, Home and the build toasts, with AGENT_BUILDER on
  const builder = useVoyagerStore((s) => !!s.features?.agent_builder);
  React.useEffect(() => (builder ? startSummary() : undefined), [builder]);
  // the drawer and the search palette go with the shell: their open flags must not outlive it
  React.useEffect(() => () => { useChatSidebar.getState().setDrawerOpen(false); useSearchPalette.getState().setOpen(false); }, []);
  // Back and Forward leave a placeholder page (it has no address of its own)
  React.useEffect(() => {
    const leave = () => useSimpleSidebar.getState().showPlaceholder(null);
    window.addEventListener('popstate', leave);
    return () => window.removeEventListener('popstate', leave);
  }, []);
  return <Box data-testid="simple-shell" sx={{ display: 'contents' }}>
    {phone ? <>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, flexShrink: 0, bgcolor: 'var(--side)', borderBottom: '1px solid var(--line)' }}><ShowButton /></Box>
      <Drawer anchor="left" open={drawerOpen} onClose={() => useChatSidebar.getState().setDrawerOpen(false)} PaperProps={{ sx: { width: SIMPLE_SIDEBAR_WIDTH, maxWidth: '86vw' } }}>
        <SimpleSidebar inDrawer />
      </Drawer>
    </> : collapsed
      ? <Box data-testid="simple-sidebar-strip" sx={{ width: 44, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.25, bgcolor: 'var(--side)', borderRight: '1px solid var(--line)' }}><ShowButton /></Box>
      : <SimpleSidebar />}
    <FinishedToastView />
    {builder && <BuildToastView ms={FINISHED_TOAST_MS} />}
    <SearchPalette />
  </Box>;
}
